import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, {
	Fragment,
	useContext,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { flushSync } from 'react-dom';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { AnnotationInfo } from '@atlaskit/editor-plugins/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema/annotation';
import type { Step } from '@atlaskit/editor-prosemirror/transform-override';
import type { AddMarkStep } from '@atlaskit/editor-prosemirror/transform';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { Box } from '@atlaskit/primitives/compiled/box';

import {
	type MutationFunctionInteropSupportedOptions,
	type MutationUpdaterInterop,
	useMutationInterop,
} from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass.ts';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import { ThirdPartyNudge } from '@confluence/third-party-nudge';
import type { InlineCommentAuthorKnownUser } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.ts';
import type {
	CommentInlineResolveProperties,
	CommentInlineLocation as InlineCommentLocation,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useShouldCommentSidebarBeSticky } from '@confluence/inline-comments-hooks/entry-points/useShouldCommentSidebarBeSticky.ts';
import { useCommentSidebarOffset } from '@confluence/inline-comments-hooks/entry-points/useCommentSidebarOffset.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-utils/entry-points/enum.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import {
	useAnnotationsDispatch,
	useAnnotations,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import {
	useCommentsPanelState,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import {
	getOptimisticResponseForInlineReply,
	type CreateInlineReplyInteropOptions,
	useCreateInlineReplyMutation,
} from '@confluence/comments-data-manager/entry-points/useCreateInlineReplyMutation.ts';
import { useUpdateCommentInCache } from '@confluence/comments-data-manager/entry-points/useUpdateCommentInCache.ts';
import {
	buildDefaultCommentsContentStatusFilters,
	resolveInlineCommentInCacheCallback,
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useUpdateReactionInCache } from '@confluence/comments-data-manager/entry-points/useUpdateReactionInCache.ts';
import {
	VIEW_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	EDIT_INLINE_COMMENT_EXPERIENCE,
	DELETE_INLINE_COMMENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_EXPERIENCE,
	VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { usePageContentId } from '@confluence/page-context';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	clearCommentDraft,
	isOptimisticSaveEnabled,
	isOptimisticSaveEnabledForTypeAndMode,
} from '@confluence/comment/entry-points/draftUtils.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import { fg } from '@confluence/feature-gating';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { CommentDeletionLocation } from '@confluence/inline-comments-queries/entry-points/DeleteInlineCommentMutation.ts';
import {
	CommentCreationLocation,
	ContentRepresentation,
} from '@confluence/inline-comments-queries/entry-points/CreateInlineCommentMutation.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import {
	handleAgentMentionInComment,
	type AdfDocument,
} from '@confluence/agent-mention/entry-points/handleAgentMentionInComment.ts';
import { hasAgentMention } from '@confluence/agent-mention/entry-points/hasAgentMention.ts';
import { useAgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/useAgentMentionFyiNudge.ts';
import { CommentAgentSessionButtonAsync } from '@confluence/agent-mention/entry-points/CommentAgentSessionButtonAsync.ts';
import { AgentSessionsProvider } from '@confluence/agent-mention/entry-points/AgentSessionsProvider.ts';
import { useOptimisticCommentAgentSessions } from '@confluence/agent-mention/entry-points/useOptimisticCommentAgentSessions.ts';
import type { CommentAgentSessionMap } from '@confluence/agent-mention/entry-points/usePageCommentAgentSessions.ts';
import { AgentMentionFyiNudgeAsync as AgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/AgentMentionFyiNudgeAsync.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import {
	showResolvedCommentFlag,
	createHandleReopenComment,
} from '@confluence/inline-comments-common/entry-points/showResolvedCommentFlag.ts';
import { EditComment } from '@confluence/inline-comments-common/entry-points/EditComment.ts';
import { CommentBody } from '@confluence/inline-comments-common/entry-points/CommentBody.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	getTargetNodeType,
	handleResolveSuccess,
	handleDeleteSuccess,
	handleMutationFailure,
	handleCreateReplySuccess,
	getTranslatedError,
	isAlreadyDeletedError,
	isNetworkError,
	parseError,
	defaultReactionsSummary,
	resolveInlineCommentAnnotation,
	unresolveInlineCommentAnnotation,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import {
	hasContributors,
	type CommentPermissions,
	type CommentAction,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { NewReplyContainer } from '@confluence/inline-comments-common/entry-points/styled.ts';
import { i18n as inlineCommentsI18n } from '@confluence/inline-comments-common/entry-points/i18n.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { objectCommentsi18n } from '@confluence/comments-util/entry-points/objectCommentsi18n.ts';
import { constructStepForGql } from '@confluence/comments-util/entry-points/rendererAnnotations.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import { useCommentsData } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useWindowSize } from '@confluence/dom-helpers/entry-points/useWindowSize.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { CommentReplies } from '@confluence/comments-panel/entry-points/CommentReplies.ts';
import {
	getReplyEditorContent,
	getReplyToContributorUsers,
	type ReplyToContributorUsers,
} from '@confluence/comments-panel/entry-points/commentThreadHelper.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { formatTimestampForAriaLabel } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { useGetCommentStatus } from '@confluence/comments-util/entry-points/commentStatus.ts';
import { buildMutationContextForNoNetworkError } from '@confluence/comments-util/entry-points/errorUtils.ts';
import type { ReactionsMutationResults } from '@confluence/reactions/entry-points/reactionTypes.ts';
import { ResolveInlineCommentMutation } from '@confluence/inline-comments-queries/entry-points/ResolveInlineCommentMutation.graphql.ts';
import { DeleteInlineCommentMutation } from '@confluence/inline-comments-queries/entry-points/DeleteInlineCommentMutation.graphql.ts';

import type {
	InlineCommentResolveInlineCommentMutation as InlineCommentResolveInlineCommentMutation$data,
	InlineCommentResolveInlineCommentMutationVariables as InlineCommentResolveInlineCommentMutation$variables,
} from './__types__/InlineCommentResolveInlineCommentMutation.ts';
import type {
	InlineCommentDeleteInlineCommentMutation_Mutation,
	InlineCommentDeleteInlineCommentMutationVariables,
} from './__types__/InlineCommentDeleteInlineCommentMutation.ts';
import type {
	InlineCommentDeleteInlineCommentMutationWithStepMutation_Mutation,
	InlineCommentDeleteInlineCommentMutationWithStepMutationVariables,
} from './__types__/InlineCommentDeleteInlineCommentMutationWithStepMutation.ts';
import {
	DIRECT_COMMENT_DATA_FLOW_METRIC,
	EDITOR_INLINE_COMMENT_RENDER_METRIC,
	RENDERER_INLINE_COMMENT_RENDER_METRIC,
} from './perf.config.ts';
import { type CommentNavigationOptions, CommentSidebar } from './CommentSidebar.tsx';

const relativeFilePath = 'annotation-provider-inline-comments/InlineComment';

const InlineCommentDeleteInlineCommentMutationWithStepMutation = graphql`
	mutation InlineCommentDeleteInlineCommentMutationWithStepMutation(
		$input: DeleteInlineCommentInput!
	) {
		deleteInlineComment(input: $input)
	}
`;
const i18n = defineMessages({
	commentLoadError: {
		id: 'annotation-provider-inline-comments.error.comment.load.message',
		defaultMessage:
			'Something went wrong when loading this comment. Refresh to view the latest updates.',
		description:
			'A generic message to inform the user that there was an error in loading a comment',
	},
	commentLoadNetworkError: {
		id: 'annotation-provider-inline-comments.network.error.message',
		defaultMessage:
			"This comment wasn't loaded due to a network error. Check your connection and try again.",
		description: 'A message to inform the user that there was a network error in loading a comment',
	},
	commentLoadErrorTitle: {
		id: 'annotation-provider-inline-comments.error.comment.load.title',
		defaultMessage: 'Unable to load comment',
		description:
			'A title for the generic message informing the user that there was an error in loading a comment',
	},
	commentReplyAriaLabel: {
		id: 'annotation-provider-inline-comments.comment.reply.aria-label',
		defaultMessage: 'Reply to {name}’s thread',
		description: 'Reply placeholder for comment thread, with the comment author name',
	},
	commentAriaLabel: {
		id: 'annotation-provider-inline-comments.comment.aria-label',
		defaultMessage: 'Comment by {displayName}, {timestamp}',
		description: 'Comment aria label, with the comment author name and timestamp',
	},
});

type ActionResult =
	| {
			step: Step;
			doc: JSONDocNode;
	  }
	| false;
type DeleteOptions = {
	deleteAnnotation: (annotationInfo: AnnotationInfo) => ActionResult;
	onDeleteSuccess: (doc: JSONDocNode, annotationId: string) => void;
};

type InlineCommentProps = {
	editorActions?: EditorActions;
	/** Rendered below the thread, after the replies. */
	footerComponent?: React.ReactNode;
	isEditor?: boolean;
	isArchived?: boolean;
	onNavigationClick: (nextMarkerRef: string) => void;
	annotation: AnnotationInfo;
	/**
	 * Return a list of inline node types, which are wrapped by the annotation,
	 * for annotation with given ID.
	 *
	 * The `undefined` will be returned if `editor_inline_comments_on_inline_nodes` is off.
	 *
	 * @todo: Do not forget to remove `undefined` when the
	 *        `editor_inline_comments_on_inline_nodes` is removed.
	 */
	getInlineNodeTypes: (annotationId: string) => string[] | undefined;
	onResolve: (annotationId: string) => void;
	deleteOptions?: DeleteOptions;
	onClose?: () => void;
	onDelete?: (annotationId: string) => void;
	supportedTopLevelActions?: CommentAction[];
	isOpeningMediaCommentFromToolbar?: boolean;
	inlineCommentRef?: (node: any) => void;
	isInlineCommentVisible?: boolean;
	isOffline?: boolean;
	preloadedCanAddComments?: boolean;
};

type ResolvedComment = {
	commentId: string;
	inlineMarkerRef: string;
};

const InlineCommentComponent: FC<InlineCommentProps & WithFlagsProps> = ({
	annotation: selectedAnnotation,
	onResolve,
	deleteOptions,
	onClose,
	isEditor,
	isArchived,
	onNavigationClick,
	onDelete,
	supportedTopLevelActions,
	getInlineNodeTypes,
	isOpeningMediaCommentFromToolbar,
	inlineCommentRef = () => {},
	isInlineCommentVisible = false,
	flags,
	isOffline,
	preloadedCanAddComments,
	footerComponent,
}) => {
	const pageClass = usePageClass();
	const lastSelected = useRef<string | undefined>(undefined);
	const { replyToCommentUser, replyToContributorUsers } = useCommentsPanelState();
	const { setCurrentView, setReplyToCommentId, setCurrentlySelectedThreadKey } =
		useCommentsPanelActions();
	const pageMode = useGetPageMode();
	const [contentId = ''] = usePageContentId();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const {
		focusedCommentId,
		replyToCommentId,
		editCommentId: editCommentQueryId,
	} = useInlineCommentQueryParams();

	const allowComments = useEmbeddedComments();

	const RENDER_METRIC = isEditor
		? EDITOR_INLINE_COMMENT_RENDER_METRIC
		: RENDERER_INLINE_COMMENT_RENDER_METRIC;

	if (lastSelected.current !== selectedAnnotation.id) {
		selectInlineCommentCompoundExperience.showComment.start({
			pageClass,
			annotationId: selectedAnnotation.id,
			entryPoint: 'unknown:InlineComment',
		});

		RENDER_METRIC.start();

		const attributes = {
			pageMode,
			pageClass,
			framework: InlineCommentFramework.ANNOTATION_PROVIDER,
			annotationId: selectedAnnotation.id,
			isEditor: !!isEditor,
			isArchived: !!isArchived,
			isOpeningMediaCommentFromToolbar: !!isOpeningMediaCommentFromToolbar,
			isInlineCommentVisible: !!isInlineCommentVisible,
			isOffline: !!isOffline,
			...(focusedCommentId && { focusedCommentId }),
			...(replyToCommentId && { replyToCommentId }),
			...(editCommentQueryId && { editCommentId: editCommentQueryId }),
		};

		selectInlineComment.debug(
			`InlineComment component mounted with annotation id ${selectedAnnotation.id}`,
		);

		experienceTracker.updateAttributes({
			name: VIEW_INLINE_COMMENT_EXPERIENCE,
			attributes,
		});

		if (pageClass !== 'renderer' && pageClass !== 'editor') {
			experienceTracker.abort({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				reason: 'Page class is not renderer or editor',
				checkForTimeout: true,
				publishDebugPoints: true,
				attributes: {
					stopPoint: relativeFilePath,
				},
			});
		}

		lastSelected.current = selectedAnnotation.id;
	}

	const annotationElementRef = useRef<HTMLElement | null>(null);
	const resolveWindowCloseTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const sidebarEl = useRef<HTMLDivElement | null>(null);
	const commentContainerElementRef = useRef<HTMLDivElement | null>(null);
	const [eventEmitter] = useState(() =>
		isEditor ? getEditorAnnotationEventEmitter() : getRendererAnnotationEventEmitter(),
	);
	const [shouldFocusNewReply, setShouldFocusNewReply] = useState(false);
	const { addUnresolvedInlineComment, removeUnresolvedInlineComment } = useInlineCommentsActions();
	const isLivePage = useIsCurrentPageLive();
	const { canAddComments } = useAddCommentPermissionCheck(
		contentId,
		false,
		preloadedCanAddComments,
	);

	const {
		handleClose,
		setEditorCreateProps,
		setHoverProps,
		setSelectionProps,
		setStoredCommentData,
		clearStoredCommentData,
		setCommentSidebarContext,
	} = useAnnotationsDispatch();
	const { storedCommentData } = useAnnotations();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId: currentUserId, displayName, cloudId, accessStatus } = useSessionData();
	const isGuest = accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;
	const { width: windowWidth } = useWindowSize();

	const [editCommentId, setCommentForEdit] = useState('');
	const [resolvedComment, setResolvedComment] = useState<ResolvedComment | null>(null);
	const [shouldRender, setShouldRender] = useState(false);

	const { onChange, resetContentChanged } = useCommentsContentActions();
	const {
		showFyi,
		agent,
		onContentChange: onAgentMentionContentChange,
	} = useAgentMentionFyiNudge({
		cloudId,
		enabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
	});
	const hasShownFyiRef = useRef(false);
	useEffect(() => {
		if (showFyi) {
			hasShownFyiRef.current = true;
		}
	}, [showFyi]);
	const handleContentChangeWithData = useCallback(
		(_hasChanges: boolean, adf?: AdfDocument) => {
			onAgentMentionContentChange(adf);
		},
		[onAgentMentionContentChange],
	);
	const { setActiveHighlight } = useInlineCommentsDispatchContext();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();
	const nudgeRef = useRef<HTMLDivElement | null>(null);
	const [popupsScrollableElement, setPopupsScrollableElement] = React.useState<
		HTMLElement | undefined
	>();
	const [hasParentMaxHeight, setHasParentMaxHeight] = React.useState(false);

	const { updateReactionInCache } = useUpdateReactionInCache();

	const intl = useIntl();
	const { formatMessage } = intl;

	const { isSticky } = useShouldCommentSidebarBeSticky({
		annotationElement: annotationElementRef.current,
		isEditor: !!isEditor,
	});
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const selectedAnnotationElement =
		!renderServerPlaceholder && typeof document !== 'undefined'
			? document.getElementById(selectedAnnotation.id)
			: null;
	const popupsMountPoint =
		!renderServerPlaceholder && typeof document !== 'undefined'
			? document.getElementById('editor-comments-sidebar') || document.body
			: undefined;

	const sidebarOffset = useCommentSidebarOffset({
		isEditor: isEditor || false,
		annotationElement: selectedAnnotationElement,
		isViewCommentMode: true,
		windowWidth,
		isSticky,
	});

	const handleCloseCreateComment = useCallback(() => {
		handleClose();
		setHoverProps(null);
		setSelectionProps(null);
		setEditorCreateProps(null);
	}, [handleClose, setHoverProps, setSelectionProps, setEditorCreateProps]);

	const handleOnClose = useCallback(() => {
		const isReply = editCommentId === '';

		experienceTracker.abort({
			name: isReply ? REPLY_TO_INLINE_COMMENT_EXPERIENCE : EDIT_INLINE_COMMENT_EXPERIENCE,
			reason: 'comment discarded by user',
		});

		if (!isOptimisticSaveEnabled()) {
			// Skip clearing draft when the optimistic save is enabled.
			// In fact, when it's disabled, saveCommentDraft() is never called
			// with 'reply' or 'edit' mode, so this should be safe to skip too.
			// TODO: (COMMENTS-6792) Remove this entirely when optimistic save is rolled out.
			clearCommentDraft(isEditor ? 'edit-inline' : 'inline', isReply ? 'reply' : 'edit');
		}
		setShouldFocusNewReply(false);

		onClose && onClose();

		setActiveHighlight(null);
	}, [experienceTracker, editCommentId, isEditor, onClose, setActiveHighlight]);

	// For now, this is a function that takes a boolean and returns a function.
	// The mustSetActiveHighlight parameter and the extra layer of nesting can be
	// removed after confluence_comment_resolve_kbs_fix is rolled out and cleaned up.
	const handleOnCloseUnified = useCallback(
		(mustSetActiveHighlight: boolean) => () => {
			if (isEditor) {
				handleOnClose();
			} else {
				onClose && onClose();
				if (
					mustSetActiveHighlight ||
					expValEquals('confluence_comment_resolve_kbs_fix', 'isEnabled', true)
				) {
					setActiveHighlight(null);
				}
			}
		},
		[isEditor, handleOnClose, onClose, setActiveHighlight],
	);

	const onlyHasReactionsSummaryErrors = useCallback((error: any) => {
		let hasOtherGqlErrors = false;
		if (error.graphQLErrors?.length > 0) {
			error.graphQLErrors.forEach((e: any) => {
				if (e.path?.includes('reactionsSummary')) {
					selectInlineComment.debug('found reactionsSummary graphql error', {
						errorMessage: e.message,
					});
				} else {
					hasOtherGqlErrors = true;
				}
			});
		}
		return !hasOtherGqlErrors && !error.networkError;
	}, []);

	const handleGqlError = useCallback(
		(error: Error) => {
			selectInlineComment.debug('received a gql error', { errorMessage: error.message });

			if (onlyHasReactionsSummaryErrors(error)) {
				selectInlineComment.debug('did not receive non-reactionsSummary errors, ignoring');
				return;
			}

			// If the error is reactions based, just set it to null and move on
			if (error.message.search(/reactions/i) !== -1 || error.message.search(/cc-pages/i) !== -1) {
				markErrorAsHandled(error);
			}

			const parsedError = parseError(error);
			const translatedError = getTranslatedError(parsedError.message);

			if (translatedError === inlineCommentsI18n.unexpectedError) {
				void flags.showWarningFlag({
					title: <FormattedMessage {...i18n.commentLoadErrorTitle} />,
					description: <FormattedMessage {...i18n.commentLoadError} />,
				});

				selectInlineCommentCompoundExperience.showComment.fail(error);

				experienceTracker.stopOnError({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					error,
					attributes: {
						stopPoint: relativeFilePath,
					},
				});
			} else {
				void flags.showWarningFlag({
					title: <FormattedMessage {...i18n.commentLoadErrorTitle} />,
					description: (
						<FormattedMessage
							{...(isNetworkError(translatedError)
								? i18n.commentLoadNetworkError
								: translatedError)}
						/>
					),
				});

				selectInlineCommentCompoundExperience.showComment.abort({
					abortReason: parsedError.message as any,
				});

				experienceTracker.abort({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					reason: parsedError.message,
					checkForTimeout: true,
					publishDebugPoints: true,
					attributes: {
						stopPoint: relativeFilePath,
					},
				});

				markErrorAsHandled(error);
			}

			selectInlineComment.debug('Closing create comment following a gql error');
			// Close the create comment component if there was an error loading the newly created comment
			handleCloseCreateComment();
		},
		[experienceTracker, flags, handleCloseCreateComment, onlyHasReactionsSummaryErrors],
	);

	const [{ unreadCommentIDSet }] = useUnreadComments();

	// Determine if we should use cache-only policy or skip query entirely
	const shouldSkipQuery = Boolean(storedCommentData);

	// Use useCommentById with markerRef to find comment directly
	const commentByMarkerRef = useCommentById({
		markerRef: selectedAnnotation.id,
		contentId,
		contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
		options: {
			skip: shouldSkipQuery,
			fetchPolicy: 'cache-first',
		},
	});

	// Use useAllCommentsData to get deleteCommentInCache function
	const { deleteCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true,
		},
	});

	// Use initial comment data, new hook data, or query data
	const effectiveData = useMemo(() => {
		if (commentByMarkerRef?.comment) {
			return { comments: { nodes: [commentByMarkerRef.comment] } };
		}
		if (shouldSkipQuery) {
			return { comments: { nodes: [storedCommentData] } };
		}
		return undefined;
	}, [shouldSkipQuery, storedCommentData, commentByMarkerRef?.comment]);
	const [reopenInlineCommentFn] = useMutationInterop<
		InlineCommentResolveInlineCommentMutation$data,
		InlineCommentResolveInlineCommentMutation$variables
	>(ResolveInlineCommentMutation, buildMutationContextForNoNetworkError());

	// Get the update function for the comments data manager
	const { updateCommentInCache } =
		useUpdateCommentInCache<InlineCommentResolveInlineCommentMutation$data>();

	const { pageInfo, error: pageInfoError } = usePageInfo();

	useEffect(() => {
		if (pageInfoError) {
			handleGqlError(pageInfoError);
		}
	}, [pageInfoError, handleGqlError]);

	// Surface comment-load errors from the data manager (useCommentById) path.
	useEffect(() => {
		if (commentByMarkerRef?.error) {
			handleGqlError(commentByMarkerRef.error);
		}
	}, [commentByMarkerRef?.error, handleGqlError]);

	// When the selectedAnnotation changes make sure we unset whatever resolved logic exists
	useEffect(() => {
		setResolvedComment(null);
		clearTimeout(resolveWindowCloseTimeout.current);
		resolveWindowCloseTimeout.current = undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAnnotation]);

	const [resolveInlineCommentFn] = useMutationInterop<
		InlineCommentResolveInlineCommentMutation$data,
		InlineCommentResolveInlineCommentMutation$variables
	>(ResolveInlineCommentMutation, buildMutationContextForNoNetworkError());

	const [deleteInlineCommentFn] = useMutationInterop<
		InlineCommentDeleteInlineCommentMutation_Mutation,
		InlineCommentDeleteInlineCommentMutationVariables
	>(DeleteInlineCommentMutation, buildMutationContextForNoNetworkError());
	const [deleteInlineCommentWithStepFn] = useMutationInterop<
		InlineCommentDeleteInlineCommentMutationWithStepMutation_Mutation,
		InlineCommentDeleteInlineCommentMutationWithStepMutationVariables
	>(
		InlineCommentDeleteInlineCommentMutationWithStepMutation,
		buildMutationContextForNoNetworkError(),
	);
	const { createInlineReplyInterop } = useCreateInlineReplyMutation({ contentId });

	// Tracks pending reply submission for coordinated editor reset.
	// When set, useLayoutEffect will call onReplyProcessed when the expected reply appears in the list.
	const pendingReplyRef = useRef<{
		expectedReplyId: string;
		hasAgentMention: boolean;
		onReplyProcessed: () => void;
	} | null>(null);
	// Editor setup
	const spaceId = pageInfo?.space?.id ?? '';
	const pageType = pageInfo?.type ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	const spaceOperations = fg('confluence_comments_space_operations_permissions')
		? pageInfo?.space?.operations || []
		: [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment =
		operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
		spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment');

	const hasMediaUploadPermissions = canAddComment && canAddAttachment;

	// Comment metadata
	let commentData;
	if (commentByMarkerRef?.comment) {
		commentData = commentByMarkerRef.comment; // Use comment from useCommentById
	} else if (shouldSkipQuery) {
		commentData = storedCommentData; // Use initial comment data directly
	} else {
		commentData = effectiveData?.comments?.nodes?.[0];
	}

	// Track experiment completion when comment data is loaded and rendered
	useEffect(() => {
		// Only track when we have comment data and the component is visible
		// And only when the experiment is running
		if (commentData && isInlineCommentVisible) {
			experienceTracker.succeed({
				name: VIEW_PAGE_DIRECT_COMMENT_DATA_FLOW_EXPERIENCE,
				attributes: {
					annotationId: selectedAnnotation.id,
					pageId: contentId,
					pageMode: isEditor ? PageMode.EDIT : PageMode.VIEW,
					isEditor: Boolean(isEditor),
				},
			});

			DIRECT_COMMENT_DATA_FLOW_METRIC.stop();
		}
	}, [
		commentData,
		isInlineCommentVisible,
		storedCommentData,
		selectedAnnotation.id,
		contentId,
		isEditor,
		experienceTracker,
	]);

	const commentStatus = useGetCommentStatus({
		resolveProperties:
			(
				commentData?.location as {
					inlineResolveProperties?: CommentInlineResolveProperties;
				}
			)?.inlineResolveProperties ||
			(
				commentData?.location as {
					commentResolveProperties?: CommentInlineResolveProperties;
				}
			)?.commentResolveProperties,
	});

	const topCommentUserId = (commentData?.author as InlineCommentAuthorKnownUser)?.accountId;
	const topCommentUserAvatar = commentData?.author?.profilePicture?.path;
	const topCommentDisplayName =
		commentData?.author?.displayName ?? formatMessage(commentsi18n.anonymous);
	const commentId = commentData?.id;
	const createdAt = commentData?.createdAt?.value;
	const createdAtNonLocalized = commentData?.createdAtNonLocalized;
	const when = commentData?.version?.when;
	const versionByDisplayName = commentData?.version?.by?.displayName;
	const isEdited = (commentData?.version?.number || 0) > 1;
	const permissions = commentData?.permissions;
	const content = commentData?.body?.value;
	const permissionType = commentData?.author?.permissionType;
	const numReplies = commentData?.replies?.length || 0;
	const commentThreadLength = 1 + numReplies; // includes root comment

	// Agent session — mirror the page's agent-session map into local state (populated by
	// the AgentSessionsProvider mounted on this component's return via onSessionMapChange).
	// We read it here so the connector/thread line can be forced on when this comment has
	// an agent session even if it has no replies. Gate inlined per
	// confluence-feature-gating rules.
	const [agentSessionMap, setAgentSessionMap] = useState<CommentAgentSessionMap | null>(null);
	const optimisticSessions = useOptimisticCommentAgentSessions([commentId ?? '']);
	const parentHasAgentSession =
		Boolean(commentId && (agentSessionMap?.get(commentId) || optimisticSessions.has(commentId))) &&
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true);

	const resolveProperties =
		(commentData?.location as InlineCommentLocation)?.inlineResolveProperties || undefined;
	const commentUrl = isEditor && !isLivePage ? commentData?.links.editui : commentData?.links.webui;
	const reactionsSummary = commentData?.reactionsSummary ?? null;
	const contributors = hasContributors(commentData) ? commentData.contributors : undefined;

	// Detect when the expected reply appears in the list and call onReplyProcessed
	// before paint, so user never sees editor + new reply at the same time.
	useLayoutEffect(() => {
		const pending = pendingReplyRef.current;
		if (!pending) {
			return;
		}
		const replies = commentData?.replies ?? [];
		const expectedReplyVisible = replies.some(
			(r: { id?: string }) => r.id === pending.expectedReplyId,
		);
		if (!expectedReplyVisible) {
			return;
		}
		pendingReplyRef.current = null;
		if (pending.hasAgentMention) {
			return;
		}
		flushSync(() => pending.onReplyProcessed());
	}, [commentData?.replies]);

	const [
		{ orderedActiveAnnotationIdList },
		{ reopenParentComment, setOrderedActiveAnnotationIdList },
	] = useCommentsData();

	useEffect(() => {
		if (commentId) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'inlineCommentsSidebar',
					objectType: 'page',
					objectId: contentId,
					source: isEditor ? 'editPageScreen' : 'viewPageScreen',
					attributes: {
						mode: pageMode,
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						commentsDeckSize: numReplies + 1, // +1 for the parent comment
						commentId,
					},
				},
			}).fire();

			selectInlineComment.debug(
				`Comment ID or page ID was updated. Comment ID: ${commentId}, page ID: ${contentId}`,
			);
		}
		// We really only need to depend on these two things and want this to fire when ONLY they change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, commentId]);

	useEffect(() => {
		if (editCommentQueryId) {
			setCommentForEdit(editCommentQueryId);
		}
	}, [editCommentQueryId]);

	useEffect(() => {
		const isReplyToCommentInChain = commentData?.replies
			?.map((reply: ReplyData) => reply?.id)
			?.includes(replyToCommentId);
		setShouldFocusNewReply(
			Boolean(replyToCommentId) && (isReplyToCommentInChain || replyToCommentId === commentId),
		);
	}, [replyToCommentId, commentId, commentData]);

	useEffect(() => {
		if (shouldFocusNewReply) {
			// for parent comment
			if (commentData && replyToCommentId === commentData.id) {
				handleReplyToComment(commentData?.id, commentData as CommentData);
			} else {
				const replyObj = commentData?.replies?.find(
					(reply: ReplyData) => reply?.id === replyToCommentId,
				);
				if (replyObj && replyObj.parentId) {
					handleReplyToComment(replyObj.parentId, replyObj as ReplyData);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldFocusNewReply, commentData?.replies]);

	useEffect(() => {
		if (effectiveData && pageInfo) {
			selectInlineComment.debug(
				'Closing create comment as the view component is ready [!apollo_loading]',
			);
			// Close the create comment component when the view component is ready
			handleCloseCreateComment();

			setShouldRender(true);

			selectInlineComment.debug(
				'Data has loaded. Rendering inline comment sidebar... [apollo_loading]',
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveData, pageInfo]);

	useEffect(() => {
		return () => {
			setShouldRender(false);
			selectInlineComment.debug('Data changed, hiding comment', {
				...(!!commentId && { commentId }),
				pageId: contentId,
				selectedAnnotationId: selectedAnnotation.id,
				shouldRender,
			});

			selectInlineComment.debug(
				`Data has changed. Selected annotation ID: ${selectedAnnotation?.id}, comment ID: ${commentId}, page ID: ${contentId}, shouldRender: ${shouldRender}. Hiding inline comment sidebar (if open)...`,
			);

			if (shouldRender) {
				selectInlineCommentCompoundExperience.abort({
					abortReason: 'Inline comment experience received a new experience while already running',
				});
				experienceTracker.abort({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					reason: `Inline comment component has received new data and is hiding the inline comment sidebar. shouldRender: ${shouldRender}. This is most likely because the user has navigated between inline comments too quickly using the keyboard shortcuts. This is an expected abort experience`,
					checkForTimeout: true,
					publishDebugPoints: true,
					attributes: {
						stopPoint: relativeFilePath,
					},
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [commentId, contentId, selectedAnnotation]);

	// Reset commentSidebarContext when view sidebar unmounts so next open is treated as view
	useEffect(() => {
		return () => {
			setCommentSidebarContext(null);
		};
	}, [setCommentSidebarContext]);

	useEffect(() => {
		// if the user has been shown the resolved comment view, but navigated away to another comment
		// we need to cancel the autodismiss functionality, as it will close this entire view and not just the
		// resolved comment notification
		if (commentId !== resolvedComment?.commentId) {
			clearTimeout(resolveWindowCloseTimeout.current);
			resolveWindowCloseTimeout.current = undefined;
		}
	}, [commentId, resolvedComment, resolveWindowCloseTimeout]);

	const toggleCommentMode = (id = '') => {
		setCommentForEdit(id);
	};

	useEffect(() => {
		const markerRef = (commentData?.location as InlineCommentLocation)?.inlineMarkerRef;
		if (markerRef) {
			setActiveHighlight(markerRef);
		}
	}, [commentData, setActiveHighlight]);

	const handleReplyToComment = (id: string, commentReplyData?: CommentData | ReplyData) => {
		const referencedData = commentReplyData || commentData;
		let contributorUsers: ReplyToContributorUsers | undefined;

		if (
			hasContributors(referencedData) &&
			expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
		) {
			contributorUsers = getReplyToContributorUsers(referencedData.contributors);
		}

		setShouldFocusNewReply(true);
		setReplyToCommentId({
			parentCommentId: id,
			location: LocationValue.STANDALONE,
			user: {
				displayName: referencedData?.author?.displayName,
				accountId: (referencedData?.author as InlineCommentAuthorKnownUser)?.accountId,
			},
			contributorUsers,
		});
	};

	const deleteCommentInCacheOnUpdate = useCallback(
		(removalCommentId: string, isReply: boolean) =>
			deleteCommentInCache({
				commentId: removalCommentId,
				onError: (err) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: err,
						commentId: removalCommentId,
					});
				},
				onCacheMiss: (_err: Error) => {
					if (isReply) {
						// Only show flag if deleting a reply failed to update cache,
						// because when deleting a top-level inline comment from
						// sidebar, user sees all UI updates even if cache update
						// fails (sidebar is closed and annotation is removed).
						void flags?.showWarningFlag({
							id: 'delete-inline-comment-in-cache-error',
							title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
							description: <FormattedMessage {...objectCommentsi18n.networkError} />,
						});
					}
				},
			}),
		[deleteCommentInCache, experienceTracker, flags],
	);

	// eslint-disable-next-line confluence-feature-gating/inline-usage -- Preserve a single exposure for optimistic delete; this value is intentionally reused across delete handlers.
	const isOptimisticDeleteEnabled = expVal(
		'confluence_fe_delete_comment_optimistic',
		'isEnabled',
		false,
	);

	const handleDeleteCommentError = useCallback(
		({
			err,
			isReply,
			deletedCommentId,
		}: {
			err: Error;
			isReply: boolean;
			deletedCommentId: string;
		}) => {
			const parsedError = parseError(err);
			const translatedError = getTranslatedError(parsedError.message, deletedCommentId);

			if (isAlreadyDeletedError(translatedError)) {
				void flags.showWarningFlag({
					id: deletedCommentId,
					title: <FormattedMessage {...inlineCommentsI18n.commentAlreadyDeletedTitle} />,
					description: <FormattedMessage {...translatedError} />,
				});

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'alreadyDeleted',
						actionSubject: 'comment',
						actionSubjectId: deletedCommentId,
						objectType: 'page',
						objectId: contentId,
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						attributes: {
							commentType: CommentType.INLINE,
							source: AnalyticsSource.STANDALONE,
							mode: pageMode,
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							isReply,
						},
					},
				}).fire();
			} else if (isOptimisticDeleteEnabled) {
				// show an error flag to the user when optimistic deletion fails
				void flags.showErrorFlag({
					id: deletedCommentId,
					title: <FormattedMessage {...inlineCommentsI18n.deleteErrorTitle} />,
					description: <FormattedMessage {...inlineCommentsI18n.deleteError} />,
				});
			}

			// If a parent comment, mark the annotation as ACTIVE again
			if (!isReply && isOptimisticDeleteEnabled) {
				unresolveInlineCommentAnnotation(selectedAnnotation.id, pageMode, eventEmitter);
			}
			// If a reply was optimistically deleted, add it back to the comment thread

			void handleMutationFailure({
				experienceTracker,
				experienceName: DELETE_INLINE_COMMENT_EXPERIENCE,
				error: err,
				commentId: deletedCommentId,
			});
		},
		[
			eventEmitter,
			experienceTracker,
			flags,
			contentId,
			pageMode,
			selectedAnnotation,
			isOptimisticDeleteEnabled,
			createAnalyticsEvent,
		],
	);

	const handleDeleteCommentInRenderer = useCallback(
		(parentId?: string | null, replyCommentId?: string) => {
			const isReply = Boolean(parentId);
			let deleteResult: ActionResult | undefined;
			let mtnVariables: any;

			if (isReply) {
				mtnVariables = {
					variables: {
						commentIdToDelete: replyCommentId,
						deleteFrom: CommentDeletionLocation.LIVE,
					},
				};
			} else {
				try {
					// Call deleteAnnotation(selectedAnnotation) to get step and any other required variables
					deleteResult = deleteOptions?.deleteAnnotation(selectedAnnotation);
				} catch (e) {
					experienceTracker.stopOnError({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: e,
					});
					return;
				}

				if (!deleteResult) {
					experienceTracker.stopOnError({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: new Error(`Unable to remove annotation ${selectedAnnotation.id} from document`),
					});
					return;
				}

				mtnVariables = {
					variables: {
						input: {
							commentId,
							step: constructStepForGql(deleteResult.step as AddMarkStep),
						},
					},
				};
			}

			// After deleting it will not be available, so we need to get the inline node types before deleting.
			const inlineNodeTypes = selectedAnnotation
				? getInlineNodeTypes(selectedAnnotation.id)
				: undefined;

			// top-level comment calls `deleteInlineCommentWithStepFn`
			// reply calls `deleteInlineComment`
			const deleteFn = isReply ? deleteInlineCommentFn : deleteInlineCommentWithStepFn;
			const deletedCommentId = isReply ? replyCommentId : commentId;

			const optimisticDeleteResponse = isReply
				? { deleteComment: true }
				: { deleteInlineComment: true };
			// If a parent comment, optimistically mark the annotation as resolved and
			// simply close the standalone.
			if (!isReply && isOptimisticDeleteEnabled) {
				resolveInlineCommentAnnotation(selectedAnnotation.id, pageMode, eventEmitter);
				handleOnCloseUnified(false)();
			}
			// If a reply, optimistically delete it.

			deleteFn({
				...mtnVariables,
				update: (() => deleteCommentInCacheOnUpdate(deletedCommentId, isReply)) as any,
				optimisticResponse: isOptimisticDeleteEnabled ? optimisticDeleteResponse : undefined,
			})
				.then((retVal) => {
					const deleteResultData = isReply
						? (retVal.data as InlineCommentDeleteInlineCommentMutation_Mutation | undefined)
						: (retVal.data as
								| InlineCommentDeleteInlineCommentMutationWithStepMutation_Mutation
								| undefined);

					if (
						!deleteResultData ||
						(!isReply &&
							!(
								deleteResultData as InlineCommentDeleteInlineCommentMutationWithStepMutation_Mutation
							).deleteInlineComment)
					) {
						throw new Error('No data returned from mutation');
					}

					const deleteAnnotationAndCleanUp = (annotationId: string) => {
						// Update storedCommentData if it exists
						if (storedCommentData && clearStoredCommentData) {
							if (!isReply) {
								// Deleting the main comment, clear all data
								clearStoredCommentData();
							} else if (replyCommentId) {
								// Deleting a reply, remove it from the replies array
								const updatedCommentData = {
									...storedCommentData,
									replies: (storedCommentData.replies || []).filter(
										(reply: any) => reply?.id !== replyCommentId,
									),
								};
								setStoredCommentData(updatedCommentData);
							}
						}

						if (!isReply) {
							// We handle this being false above and can't get here if it is
							if (deleteResult) {
								deleteOptions?.onDeleteSuccess(deleteResult.doc, annotationId);
							}

							// Reset the content state on successful deletion on a non-reply
							resetContentChanged();
							onClose && onClose();
						}
						experienceTracker.succeed({
							name: DELETE_INLINE_COMMENT_EXPERIENCE,
						});
					};

					handleDeleteSuccess({
						commentId: deletedCommentId,
						annotationId: selectedAnnotation?.id,
						pageId: contentId,
						pageMode,
						isReply,
						source: AnalyticsSource.STANDALONE,
						inlineNodeTypes,
						createAnalyticsEvent,
						removeUnresolvedInlineComment,
						onSuccess: deleteAnnotationAndCleanUp,
					});
				})
				.catch((err: any) => {
					handleDeleteCommentError({
						err,
						isReply,
						deletedCommentId,
					});
				});
		},
		[
			commentId,
			createAnalyticsEvent,
			deleteOptions,
			deleteCommentInCacheOnUpdate,
			eventEmitter,
			experienceTracker,
			resetContentChanged,
			onClose,
			contentId,
			isOptimisticDeleteEnabled,
			storedCommentData,
			clearStoredCommentData,
			setStoredCommentData,
			handleOnCloseUnified,
			removeUnresolvedInlineComment,
			selectedAnnotation,
			deleteInlineCommentFn,
			deleteInlineCommentWithStepFn,
			pageMode,
			getInlineNodeTypes,
			handleDeleteCommentError,
		],
	);

	const handleDeleteCommentInEditorAndLivePages = useCallback(
		(parentId?: string | null, replyCommentId?: string) => {
			const isReply = Boolean(parentId);
			const targetNodeType = getTargetNodeType(selectedAnnotation.id, true);
			const deletedCommentId = isReply ? replyCommentId : commentId;

			// If a parent comment, optimistically mark the annotation as resolved and
			// simply close the standalone.
			if (!isReply && isOptimisticDeleteEnabled) {
				resolveInlineCommentAnnotation(selectedAnnotation.id, pageMode, eventEmitter);
				handleOnCloseUnified(false)();
			}
			// If a reply, optimistically delete it.

			// both top-level comments and replies call `deleteInlineComment`
			deleteInlineCommentFn({
				variables: {
					commentIdToDelete: deletedCommentId,
					deleteFrom: isLivePage ? CommentDeletionLocation.LIVE : CommentDeletionLocation.EDITOR,
				},
				update: () => deleteCommentInCacheOnUpdate(deletedCommentId, isReply),
				optimisticResponse: isOptimisticDeleteEnabled ? { deleteComment: true } : undefined,
			})
				.then((retVal) => {
					const { data: deleteResultData } = retVal;

					if (!deleteResultData || (!isReply && !deleteResultData.deleteComment)) {
						throw new Error('No data returned from mutation');
					}

					// Update storedCommentData if it exists
					if (storedCommentData) {
						if (!isReply) {
							// Deleting the main comment, clear all data
							clearStoredCommentData();
						} else if (replyCommentId) {
							// Deleting a reply, remove it from the replies array
							const updatedCommentData = {
								...storedCommentData,
								replies: (storedCommentData.replies || []).filter(
									(reply: any) => reply?.id !== replyCommentId,
								),
							};
							setStoredCommentData(updatedCommentData);
						}
					}

					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'deleted',
							actionSubject: 'comment',
							actionSubjectId: deletedCommentId,
							objectType: 'page',
							objectId: contentId,
							source: 'editPageScreen',
							attributes: {
								commentType: CommentType.INLINE,
								source: AnalyticsSource.STANDALONE,
								pageMode,
								framework: InlineCommentFramework.ANNOTATION_PROVIDER,
								inlineNodeTypes: selectedAnnotation
									? getInlineNodeTypes(selectedAnnotation.id)
									: undefined,
								isReply,
								targetNodeType,
							},
						},
					}).fire();

					experienceTracker.succeed({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
					});

					if (!isReply) {
						onDelete?.(selectedAnnotation?.id);
						// Remove the comment from the unresolved list
						removeUnresolvedInlineComment(selectedAnnotation?.id);
						// Reset the content state on successful deletion on a non-reply
						resetContentChanged();
					}
				})
				.catch((err: any) => {
					handleDeleteCommentError({
						err,
						isReply,
						deletedCommentId,
					});
				});
		},
		[
			commentId,
			createAnalyticsEvent,
			deleteInlineCommentFn,
			deleteCommentInCacheOnUpdate,
			eventEmitter,
			experienceTracker,
			pageMode,
			contentId,
			onDelete,
			selectedAnnotation,
			getInlineNodeTypes,
			resetContentChanged,
			isLivePage,
			storedCommentData,
			clearStoredCommentData,
			setStoredCommentData,
			handleOnCloseUnified,
			isOptimisticDeleteEnabled,
			removeUnresolvedInlineComment,
			handleDeleteCommentError,
		],
	);

	// When handleDeleteComment() is called for a reply, both parentId and replyCommentId are defined
	// When called for a top-level comment, both are undefined
	const handleDeleteComment = useCallback(
		(parentId?: string | null, replyCommentId?: string) => {
			if (!isEditor) {
				handleDeleteCommentInRenderer(parentId, replyCommentId);
			} else {
				handleDeleteCommentInEditorAndLivePages(parentId, replyCommentId);
			}
		},
		[isEditor, handleDeleteCommentInRenderer, handleDeleteCommentInEditorAndLivePages],
	);

	const handleResolveMutationFailure = useCallback(
		(resolveError: Error) => {
			void handleMutationFailure({
				experienceTracker,
				experienceName: RESOLVE_INLINE_COMMENT_EXPERIENCE,
				error: resolveError,
			});
		},
		[experienceTracker],
	);

	const resolveUpdateFn: MutationUpdaterInterop<InlineCommentResolveInlineCommentMutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateCommentInCache({
					mutationResult,
					commentId,
					updateCallback: resolveInlineCommentInCacheCallback,
					onError: handleResolveMutationFailure,
				}),
			[updateCommentInCache, commentId, handleResolveMutationFailure],
		);

	const getReopenUpdateFn = useCallback(
		(onReopenCacheError: (error: Error) => void) => {
			const reopenUpdateFcn: MutationUpdaterInterop<
				InlineCommentResolveInlineCommentMutation$data
			> = (_dataProxy, mutationResult) =>
				updateCommentInCache({
					mutationResult,
					commentId,
					updateCallback: resolveInlineCommentInCacheCallback,
					onError: onReopenCacheError,
				});

			return reopenUpdateFcn;
		},
		[updateCommentInCache, commentId],
	);

	const handleResolveComment = () => {
		const mtnOptions: MutationFunctionInteropSupportedOptions<
			InlineCommentResolveInlineCommentMutation$data,
			InlineCommentResolveInlineCommentMutation$variables
		> = {
			variables: { commentId, resolved: true },
			update: resolveUpdateFn,
		};

		const annotationId = selectedAnnotation.id;
		if (expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
			// No need to set mtnOptions.optimisticResponse since we'll close the
			// standalone comment immediately, as if it is already resolved.

			// Optimistically mark the annotation as resolved
			resolveInlineCommentAnnotation(annotationId, pageMode, eventEmitter);

			// Close the standalone comment immediately as if comment is resolved
			handleOnCloseUnified(false)();
		}

		resolveInlineCommentFn(mtnOptions)
			.then(() => {
				// this will be removed when the prop becomes optional
				onResolve(annotationId);

				handleResolveSuccess({
					commentId,
					parentCommentMarkerRef: annotationId,
					pageId: contentId,
					pageMode,
					eventEmitter,
					source: AnalyticsSource.STANDALONE,
					removeUnresolvedInlineComment,
					createAnalyticsEvent,
					getInlineNodeTypes,
					commentType: CommentType.INLINE,
				});

				setOrderedActiveAnnotationIdList(
					orderedActiveAnnotationIdList
						.map((status) => status.threadKey)
						.filter((id) => id !== annotationId),
				);

				experienceTracker.succeed({
					name: RESOLVE_INLINE_COMMENT_EXPERIENCE,
				});

				if (!expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
					// If optimistic resolve is enabled, standalone comment should have been closed already.
					handleOnCloseUnified(false)();
				}

				if (allowComments) {
					showResolvedCommentFlag({
						commentId: commentId ?? '',
						experienceTracker,
						flags,
						mode: pageMode,
						annotationId,
						formatMessage,
						showCommentsPanel,
						setCurrentView,
						isCommentsPanelShown,
						handleReopenComment:
							createHandleReopenComment<InlineCommentResolveInlineCommentMutation$data>({
								reopenInlineCommentFn,
								getUpdateFn: getReopenUpdateFn,
								commentId: commentId ?? '',
								annotationId,
								reopenParentComment,
								setOrderedActiveAnnotationIdList,
								orderedActiveAnnotationIdList,
								highlightReopenedComment,
								isEditor: isEditor ?? false,
								eventEmitter,
								setActiveHighlight,
								experienceTracker,
							}),
						setCurrentlySelectedThreadKey,
					});
				}

				// Show the resolved comment window if not in editor
				if (!isEditor) {
					if (commentId) {
						setResolvedComment({
							commentId,
							inlineMarkerRef: annotationId,
						});
					}

					// If the comment we just resolved is the same as the one we're currently viewing, set a timeout
					// to auto-close the container as it should be showing the resolved comment notification view
					if (resolvedComment?.commentId === commentData?.id) {
						resolveWindowCloseTimeout.current = setTimeout(() => {
							onClose && onClose();
						}, 7500);
					}
				}
			})
			.catch((err: any) => {
				if (expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
					// Undo resolving the annotation when optimistic resolve is enabled
					unresolveInlineCommentAnnotation(annotationId, pageMode, eventEmitter);

					// Don't re-open the standalone comment box. Show an error flag instead.
					void flags.showErrorFlag({
						id: commentId,
						title: formatMessage(inlineCommentsI18n.resolveErrorTitle),
						description: formatMessage(inlineCommentsI18n.resolveError),
					});
				}
				handleResolveMutationFailure(err);
			});
	};

	const onCancelComment = () => {
		setReplyToCommentId();
		setShouldFocusNewReply(false);
	};

	const handlePublishFail = useCallback(
		(error: Error) => {
			// Clear pending reply state on failure so useLayoutEffect doesn't
			// incorrectly trigger editor reset on a later unrelated data update
			pendingReplyRef.current = null;
			return handleMutationFailure({
				experienceTracker,
				experienceName: REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				error,
				commentId,
				shouldReturnError: true,
				shouldAbortForExpectedError: true,
				compoundExperienceName: REPLY_TO_INLINE_COMMENT_EXPERIENCE,
			});
		},
		[experienceTracker, commentId],
	);

	const handleReplyCacheMiss = useCallback(
		(_error: Error) => {
			void flags?.showWarningFlag({
				title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
				description: <FormattedMessage {...objectCommentsi18n.networkError} />,
			});
		},
		[flags],
	);

	const handleCreateReplyUnified = async (adf: object, onSuccess: () => void) => {
		const parentCommentId = commentId ?? '';
		// eslint-disable-next-line confluence-feature-gating/inline-usage
		const variables = {
			input: {
				containerId: contentId,
				parentCommentId,
				commentBody: {
					value: JSON.stringify(adf),
					representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
				},
				createdFrom: isLivePage
					? CommentCreationLocation.LIVE
					: isEditor
						? CommentCreationLocation.EDITOR
						: CommentCreationLocation.RENDERER,
			},
			pageId: contentId,
		};

		startEditorSubExperience({
			name: REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
			attributes: {
				parentCommentId,
				isGuest,
			},
		});

		// Create the inline reply via the Comments Data Manager interop.
		const replyCreationInteropWrapper = (options: CreateInlineReplyInteropOptions) =>
			createInlineReplyInterop({
				options,
				onCacheUpdateError: handlePublishFail,
				onCacheMiss: handleReplyCacheMiss,
			});

		const hasAgentMentionInReply =
			hasAgentMention(adf as AdfDocument) &&
			expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true);
		const isOptimisticReplyEnabled =
			!hasAgentMentionInReply &&
			isOptimisticSaveEnabledForTypeAndMode(isEditor ? 'edit-inline' : 'inline', 'reply');

		if (isOptimisticReplyEnabled) {
			// Optimistically close reply editor as if mutation succeeded immediately
			onSuccess?.();
		}

		return replyCreationInteropWrapper({
			variables,
			optimisticResponse: isOptimisticReplyEnabled
				? getOptimisticResponseForInlineReply({
						userId: currentUserId ?? '',
						displayName: displayName ?? '',
						adf,
						pageId: contentId,
						parentCommentId,
						parentCommentAnnotationId: selectedAnnotation.id,
					})
				: undefined,
		})
			.then(({ data: replyData }: any) => {
				const replyInlineComment = replyData.replyInlineComment;
				if (expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
					if (replyInlineComment?.id) {
						handleAgentMentionInComment(
							adf as AdfDocument,
							cloudId,
							contentId,
							replyInlineComment.id,
							currentUserId ?? undefined,
							'inline',
						);
					}
				}
				if (!replyInlineComment?.reactionsSummary) {
					replyInlineComment.reactionsSummary = defaultReactionsSummary;
				}
				// Update storedCommentData if it exists by adding the new reply
				if (storedCommentData && setStoredCommentData) {
					const updatedCommentData = {
						...storedCommentData,
						replies: [...(storedCommentData.replies || []), replyInlineComment],
					};
					setStoredCommentData(updatedCommentData);
				}

				// reset the replyToCommentUser if any
				replyToCommentUser && setReplyToCommentId();

				if (!isOptimisticReplyEnabled) {
					pendingReplyRef.current = {
						expectedReplyId: replyInlineComment.id,
						hasAgentMention: hasAgentMentionInReply,
						onReplyProcessed: onSuccess,
					};
				}

				// The reply saved — collapse the agent-mention FYI nudge. The editor content clears on
				// save without emitting a content-change, so reset the nudge explicitly (otherwise the
				// "…will respond in Chat" banner lingers above the reply box after send).
				onAgentMentionContentChange(undefined);

				handleCreateReplySuccess({
					// Defer onSuccess (resetEditor) to useLayoutEffect
					// so editor collapses in the same paint as the new reply appearing
					onSuccess: undefined,
					editCommentQueryId,
					setCommentForEdit,
					data: replyData,
					createAnalyticsEvent,
					pageId: contentId,
					pageType,
					analyticsSource: AnalyticsSource.STANDALONE,
					commentId,
					pageMode,
					selectedAnnotation,
					getInlineNodeTypes: undefined,
					experienceTracker,
					isEditor: !!isEditor,
				});
			})
			.catch((error: any) => {
				return handlePublishFail(error);
			});
	};

	const updateReactionsCacheFn = useCallback(
		(
			emojiId: string,
			actionType: 'add' | 'delete',
			objectId: string,
			_containerId: string | undefined,
			result?: ReactionsMutationResults,
			onCacheUpdateError?: (error: Error) => void,
		) => {
			// Update the GQL cache when using the new Comments Data Manager
			// Mutation result should exist, but this check is to satisfy TypeScript
			if (result) {
				return updateReactionInCache({
					mutationResult: result,
					commentId: objectId,
					pageId: contentId,
					contentType: 'page',
					actionType,
					emojiId,
					onError: onCacheUpdateError,
				});
			}

			// Also update storedCommentData if it exists
			if (storedCommentData && setStoredCommentData) {
				const updatedCommentData = { ...storedCommentData };

				// Find if this is the main comment or a reply
				if (storedCommentData.id === objectId) {
					// Update main comment reactions
					const currentReactions =
						storedCommentData.reactionsSummary?.reactionsSummaryForEmoji || [];
					const updatedReactions = [...currentReactions];
					let reactionsCount = storedCommentData.reactionsSummary?.reactionsCount || 0;

					if (actionType === 'add') {
						const existingReaction = updatedReactions.find((r: any) => r?.emojiId === emojiId);
						if (existingReaction && !existingReaction.reacted) {
							existingReaction.count++;
							existingReaction.reacted = true;
						} else if (!existingReaction) {
							updatedReactions.push({
								emojiId,
								count: 1,
								reacted: true,
								id: `${storedCommentData.reactionsSummary?.containerAri}|${storedCommentData.reactionsSummary?.ari}|${emojiId}`,
								__typename: 'ReactionsSummaryForEmoji',
							});
						}
						reactionsCount++;
					} else if (actionType === 'delete') {
						const reactionIdx = updatedReactions.findIndex((r: any) => r?.emojiId === emojiId);
						if (reactionIdx !== -1) {
							const reaction = updatedReactions[reactionIdx];
							if (reaction && reaction.count > 1) {
								reaction.count--;
								reaction.reacted = false;
							} else {
								updatedReactions.splice(reactionIdx, 1);
							}
							reactionsCount--;
						}
					}

					updatedCommentData.reactionsSummary = {
						...storedCommentData.reactionsSummary,
						reactionsSummaryForEmoji: updatedReactions,
						reactionsCount,
						ari: storedCommentData.reactionsSummary?.ari || '',
					} as typeof storedCommentData.reactionsSummary;
				} else {
					// Update reply reactions
					updatedCommentData.replies = (storedCommentData.replies || []).map((reply: any) => {
						if (reply?.id === objectId) {
							const currentReactions = reply.reactionsSummary?.reactionsSummaryForEmoji || [];
							const updatedReactions = [...currentReactions];
							let reactionsCount = reply.reactionsSummary?.reactionsCount || 0;

							if (actionType === 'add') {
								const existingReaction = updatedReactions.find((r: any) => r?.emojiId === emojiId);
								if (existingReaction && !existingReaction.reacted) {
									existingReaction.count++;
									existingReaction.reacted = true;
								} else if (!existingReaction) {
									updatedReactions.push({
										emojiId,
										count: 1,
										reacted: true,
										id: `${reply.reactionsSummary?.containerAri}|${reply.reactionsSummary?.ari}|${emojiId}`,
										__typename: 'ReactionsSummaryForEmoji',
									});
								}
								reactionsCount++;
							} else if (actionType === 'delete') {
								const reactionIdx = updatedReactions.findIndex((r: any) => r?.emojiId === emojiId);
								if (reactionIdx !== -1) {
									const reaction = updatedReactions[reactionIdx];
									if (reaction && reaction.count > 1) {
										reaction.count--;
										reaction.reacted = false;
									} else {
										updatedReactions.splice(reactionIdx, 1);
									}
									reactionsCount--;
								}
							}

							return {
								...reply,
								reactionsSummary: {
									...reply.reactionsSummary,
									reactionsSummaryForEmoji: updatedReactions,
									reactionsCount,
									ari: reply.reactionsSummary?.ari || '',
								} as typeof reply.reactionsSummary,
							};
						}
						return reply;
					});
				}

				setStoredCommentData(updatedCommentData);
			}
		},
		[contentId, storedCommentData, setStoredCommentData, updateReactionInCache],
	);

	const highlightReopenedComment = useCallback(
		(annotationId: string) => {
			// Emit the event to re-highlight the mark
			if (expVal('confluence_fe_resolve_reopen_comment_refactor', 'isEnabled', false)) {
				unresolveInlineCommentAnnotation(annotationId, pageMode, eventEmitter);
			} else {
				eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
					[annotationId]: {
						id: annotationId,
						annotationType: AnnotationTypes.INLINE_COMMENT,
						state: AnnotationMarkStates.ACTIVE,
					},
				});
			}

			addUnresolvedInlineComment(annotationId, pageMode);
		},
		[addUnresolvedInlineComment, eventEmitter, pageMode],
	);

	const navigationOptions = useMemo(
		() => ({
			commentData: commentData as any,
			onNavigationClick,
		}),
		[commentData, onNavigationClick],
	);

	useEffect(() => {
		if (!selectedAnnotation.id) {
			return;
		}

		if (shouldRender === false) {
			// The UI has not started rendering yet, so we don't want to check if it's visible.
			selectInlineComment.debug(
				`InlineComment has been rendered, but is returning null due to shouldRender. Selected annotation ID: ${selectedAnnotation?.id}`,
			);

			return;
		}

		if (renderServerPlaceholder) {
			// Nothing has rendered yet, and the placeholder clears in a transition, so the sidebar
			// commits in a later render than this one. Checking now would race that commit.
			return;
		}

		const initialTaskId = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.taskId;

		// Add a guard for rapid changes
		let isCancelled = false;

		const timeoutId = setTimeout(() => {
			// There are multiple apis and effects involved in swapping from the create comment
			// ui to the view comment ui.
			// This works around this by only marking the experience as complete after
			// a short timeout + animation frame.

			if (isCancelled) return;

			requestAnimationFrame(() => {
				if (isCancelled) return;

				const currentTaskId = experienceTracker.getExperienceState(
					SELECT_INLINE_COMMENT_EXPERIENCE,
				)?.taskId;

				if (!currentTaskId) {
					// Note -- this should not do anything (as debug points are only added to active experiences)
					selectInlineComment.debug('No current task ID');
					return;
				}

				if (initialTaskId !== currentTaskId) {
					// The experience has been restarted or has otherwise ended.
					experienceTracker.attachDebugPoint({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						message: `Experience has been restarted or has otherwise ended. initialTaskId: ${initialTaskId}, currentTaskId: ${currentTaskId}`,
					});
					return;
				}

				if (lastSelected.current !== selectedAnnotation.id) {
					// The selected annotation has changed, this should have led to an earlier abort
					// (as an overlapping experience is started)
					selectInlineComment.debug(
						`View inline comment assumed aborted due to selection change. lastSelected.current: ${lastSelected?.current}, selectedAnnotation.id: ${selectedAnnotation?.id}`,
					);
					return;
				}

				if (!annotationElementRef.current) {
					// We'd expect the select annotation experience to fail
					// if no annotation is added to the document
					// Assume this experience has aborted (or it will timeout)
					selectInlineComment.debug(
						'Show inline comment assumed aborted by deselection or selection change. This message indicate an abort failed and something has gone wrong.',
					);
					selectInlineCommentCompoundExperience.abort({
						abortReason: 'No annotation element found for the inline comment ui to attach to.',
					});
					experienceTracker.abort({
						name: VIEW_INLINE_COMMENT_EXPERIENCE,
						reason: 'No annotation element found for the inline comment component to attach to.',
						checkForTimeout: true,
						publishDebugPoints: true,
						attributes: {
							stopPoint: relativeFilePath,
						},
					});
					void flags.showWarningFlag({
						title: <FormattedMessage {...i18n.commentLoadErrorTitle} />,
						description: <FormattedMessage {...i18n.commentLoadError} />,
					});
					return;
				}

				if (!sidebarEl.current) {
					selectInlineCommentCompoundExperience.showComment.fail(
						new Error('Document missing inline comment ui'),
					);
					if (shouldRender) {
						experienceTracker.fail({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							error: new Error('Document is missing inline comment sidebar element'),
							attributes: {
								stopPoint: relativeFilePath,
							},
						});
					}
					return;
				}

				const commentUIVerticalDistanceFromAnnotation = Math.abs(
					annotationElementRef.current.getBoundingClientRect().top -
						sidebarEl.current.getBoundingClientRect?.().top,
				);

				// The 40 pixel allowance has been arbitrarily chosen to allow for some wiggle room
				if (commentUIVerticalDistanceFromAnnotation > 40) {
					const isVisible = sidebarEl.current.checkVisibility();
					if (isVisible) {
						// if it's fully visible, but misaligned, we don't treat this as a failure
						selectInlineComment.debug('View Inline Comment UI too far from annotation', {
							misalignedBy: commentUIVerticalDistanceFromAnnotation,
						});
					} else {
						selectInlineCommentCompoundExperience.showComment.fail(
							new Error('View Created Comment UI too far from annotation'),
							{ misalignedBy: commentUIVerticalDistanceFromAnnotation },
						);

						experienceTracker.fail({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							error: new Error('View inline comment sidebar was rendered too far from annotation'),
							attributes: {
								misalignedBy: commentUIVerticalDistanceFromAnnotation,
								stopPoint: relativeFilePath,
							},
						});
						return;
					}
				}

				experienceTracker.succeed({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					publishDebugPoints: true,
					attributes: {
						stopPoint: relativeFilePath,
					},
				});

				selectInlineCommentCompoundExperience.showComment.complete(
					'Inline Comment panel comment shown',
				);
			});
		}, 10);

		return () => {
			isCancelled = true;
			clearTimeout(timeoutId);
			setShouldRender(false);

			const currentTaskId = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_EXPERIENCE,
			)?.taskId;

			if (!currentTaskId) {
				// There is no running experience, no need to abort.
				return;
			}

			if (initialTaskId !== currentTaskId) {
				// The experience has been restarted or has otherwise ended.
				return;
			}

			// If a show inline comment experience is still running while this component is in the process of being
			// unmounted, we abort it rather than fail it.
			selectInlineCommentCompoundExperience.showComment.abort({
				abortReason: 'InlineComment component unmounted',
			});

			experienceTracker.abort({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				reason: 'Inline comment component unmounted',
				checkForTimeout: true,
				publishDebugPoints: true,
				attributes: {
					stopPoint: relativeFilePath,
				},
			});
		};
	}, [
		selectedAnnotation.id,
		shouldRender,
		renderServerPlaceholder,
		createAnalyticsEvent,
		experienceTracker,
		flags,
	]);

	if (!shouldRender) {
		return null;
	}

	const renderComment = () => {
		if (commentId === editCommentId) {
			return (
				<EditComment
					pageId={contentId}
					date={when ?? ''}
					createdAtNonLocalized={createdAtNonLocalized ?? ''}
					pageType={pageType}
					annotationId={selectedAnnotation.id}
					commentId={commentId}
					spaceId={spaceId}
					content={content ?? ''}
					isReply={false}
					displayCommentInViewMode={toggleCommentMode}
					avatarUrl={topCommentUserAvatar ?? ''}
					displayName={topCommentDisplayName}
					dateUrl={commentUrl ?? ''}
					versionDate={when ?? ''}
					isEdited={isEdited}
					resolveProperties={resolveProperties}
					mode={isEditor ? 'edit' : 'view'}
					hasMediaUploadPermissions={hasMediaUploadPermissions}
					getInlineNodeTypes={getInlineNodeTypes}
					commentType={CommentType.INLINE}
					isStandaloneComment
					hasParentMaxHeight={hasParentMaxHeight}
					numReplies={numReplies}
				/>
			);
		} else {
			const getDeleteFn = () => {
				if (!isLivePage && isEditor) {
					return undefined;
				}

				return handleDeleteComment(undefined);
			};

			return (
				<Fragment>
					<CommentBody
						userId={topCommentUserId}
						avatarUrl={topCommentUserAvatar}
						displayName={topCommentDisplayName}
						date={when ?? ''}
						dateUrl={commentUrl ?? ''}
						createdAt={createdAt ?? ''}
						createdAtNonLocalized={createdAtNonLocalized ?? ''}
						versionDate={when ?? ''}
						versionByDisplayName={versionByDisplayName}
						pageId={contentId}
						pageType={pageType}
						commentId={commentId ?? ''}
						isReply={false}
						isEdited={isEdited}
						permissions={permissions as CommentPermissions}
						deleteComment={getDeleteFn}
						editComment={() => toggleCommentMode(commentId)}
						content={content ?? ''}
						isCommentActive
						mode={isEditor ? 'edit' : 'view'}
						permissionType={permissionType ?? undefined}
						resolveComment={handleResolveComment}
						supportedActions={supportedTopLevelActions}
						isUnread={unreadCommentIDSet.has(commentId ?? '')}
						numReplies={numReplies}
						commentType={CommentType.INLINE}
						inheritedReactionsData={reactionsSummary}
						reactionsCacheUpdateFn={updateReactionsCacheFn}
						contributors={contributors}
						replyToComment={handleReplyToComment}
						hideBranchingStyle={
							!parentHasAgentSession &&
							(!commentData?.replies || commentData?.replies?.length === 0)
						}
						resolveProperties={resolveProperties}
						isStandaloneComment
						canAddComments={canAddComments}
						isOffline={isOffline}
						isFabricPage
						annotationId={selectedAnnotation?.id}
						annotatedText={annotationElementRef.current?.textContent ?? undefined}
						onRendered={() => {
							experienceTracker.attachDebugPoint({
								name: VIEW_INLINE_COMMENT_EXPERIENCE,
								message: 'Inline comment body rendered',
							});
						}}
					/>
					{/* Attach the agent-session action button below the parent comment when it has an
					    associated agent session. Code-split + gated so the chunk only loads when the
					    experiment is on (gate inlined, ordered last per feature-gating rules). */}
					{expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) && (
						<CommentAgentSessionButtonAsync
							commentId={commentId ?? ''}
							userId={topCommentUserId}
							avatarUrl={topCommentUserAvatar}
							indent
						/>
					)}
					<PageSegmentLoadEnd key={`stop-${commentId}`} metric={RENDER_METRIC} />
				</Fragment>
			);
		}
	};

	annotationElementRef.current = selectedAnnotationElement;

	// Handle case where comment has been deleted
	if (!annotationElementRef.current) {
		selectInlineComment.debug(
			'There is no annotation element element, so not rendering InlineComment',
		);
		return null;
	}

	const isCommentAuthor = topCommentUserId === currentUserId;
	const isFirstComment = commentThreadLength === 1;

	const sidebar = (
		<CommentSidebar
			nudgeRef={nudgeRef}
			pageId={contentId}
			annotationElement={annotationElementRef.current}
			navigationOptions={navigationOptions as CommentNavigationOptions}
			onClose={handleOnCloseUnified(true)}
			isEditor={isEditor}
			isViewCommentMode
			sidebarOffset={sidebarOffset}
			commentId={commentId}
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			sidebarEl={sidebarEl}
			annotationId={selectedAnnotation?.id}
			inlineCommentRef={inlineCommentRef}
			setPopupsScrollableElement={setPopupsScrollableElement}
			isSticky={isSticky}
			isFabricPage
			authorName={topCommentDisplayName}
			onMaxHeightApplied={setHasParentMaxHeight}
		>
			<Fragment>
				<Box
					testId="inline-comment-container"
					tabIndex={0}
					aria-label={formatMessage(i18n.commentAriaLabel, {
						displayName: topCommentDisplayName,
						timestamp: formatTimestampForAriaLabel({
							intl,
							dateString: commentData?.createdAtNonLocalized,
							commentStatus,
							resolveProperties:
								commentData?.location?.inlineResolveProperties ||
								commentData?.location?.commentResolveProperties,
						}),
					})}
				>
					{renderComment()}
				</Box>
				{commentData?.replies?.length > 0 && (
					<CommentReplies
						flags={isOptimisticDeleteEnabled ? flags : undefined}
						parentComment={
							{
								...commentData,
								isOpen: true,
								type: CommentType.INLINE,
								// Inlined from a helper that became the only reference after a gate removal.
								// Probably could use some future investigation, but given addition is part of FG removal, not touching logic here
								// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render
								replies: ((commentData?.replies ?? []) as ReplyData[])
									.filter((reply): reply is ReplyData => reply != null)
									.map((reply) => ({
										...reply,
										reactionsSummary: reply.reactionsSummary ?? null,
										type: CommentType.INLINE,
									})),
							} as unknown as CommentData
						}
						pageInfo={pageInfo}
						pageMode={pageMode}
						parentCommentId={commentData.id}
						canAddComments={canAddComments}
						isStandaloneComment
						editCommentQueryId={editCommentQueryId}
						selectedAnnotation={selectedAnnotation}
						key={`standalone-comment-replies-${commentData.id}`} // Needed to remount this component when we change which comment we're viewing
						deleteStandaloneComment={handleDeleteComment}
						replyToComment={handleReplyToComment}
						updateReactionsCacheFn={updateReactionsCacheFn}
						{...(popupsScrollableElement && popupsMountPoint
							? {
									popupsScrollableElement,
									popupsMountPoint,
								}
							: {})}
						hasParentMaxHeight={hasParentMaxHeight}
					/>
				)}

				{!isArchived && canAddComments && (
					<NewReplyContainer
						mode={isEditor ? 'edit' : 'view'}
						data-cy="editor-reply-container"
						data-testid="inline-comment-new-reply-container"
						ref={commentContainerElementRef}
						isFabricPage
						isStandaloneComment
					>
						{(showFyi || hasShownFyiRef.current) &&
							expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) && (
								<AgentMentionFyiNudge visible={showFyi} agent={agent} />
							)}
						<CommentEditor
							pageId={contentId}
							pageType={pageType}
							onCancelComment={onCancelComment}
							content={getReplyEditorContent({ replyToCommentUser, replyToContributorUsers })}
							appearance="chromeless"
							EditorComponent={ChromelessEditor}
							onSaveComment={handleCreateReplyUnified}
							onContentChange={onChange}
							onContentChangeWithData={handleContentChangeWithData}
							onEditorReady={() => {
								experienceTracker.succeed({
									name: REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
								});
							}}
							commentMode="reply"
							commentType={isEditor ? 'edit-inline' : 'inline'}
							spaceId={spaceId}
							showCancelButton
							useNewWarningModal
							hideWatchCheckbox
							expandEditor={shouldFocusNewReply}
							pageMode={isEditor ? 'edit' : 'view'}
							hasMediaUploadPermissions={hasMediaUploadPermissions}
							topLevelCommentId={commentId}
							shouldWarnOnInternalNavigation
							commentContainerElementRef={commentContainerElementRef}
							isStandaloneComment
							hasParentMaxHeight={hasParentMaxHeight}
							// Needed to remount this component when we change which comment we're viewing
							// Use `selectedAnnotation.id` as key instead of `commentId`, so that
							// when optimistic create is enabled, CommentEditor is not entirely
							// remounted after GQL mutation succeeds.
							key={`standalone-comment-new-reply-editor-${selectedAnnotation.id}`}
							{...(popupsScrollableElement && popupsMountPoint
								? {
										popupsScrollableElement,
										popupsMountPoint,
									}
								: {})}
							isOffline={isOffline}
							customAriaLabel={formatMessage(i18n.commentReplyAriaLabel, {
								name: topCommentDisplayName,
							})}
						/>
					</NewReplyContainer>
				)}
			</Fragment>
			{footerComponent}
			{isCommentAuthor && isFirstComment && (
				<ThirdPartyNudge
					trigger="inline-comments"
					showImage={false}
					width={280}
					reference={nudgeRef}
				/>
			)}
		</CommentSidebar>
	);

	// Mount the (non-blocking) AgentSessionsProvider per-surface — a separate refetch
	// from the footer surface — so each comment in this inline-comment popup can look up
	// its own agent session by commentId via AgentSessionsContext. The provider renders
	// the popup immediately and loads the (usually Relay-cache-warm) sessions in a
	// Suspense sibling, so the popup never blanks. The page marker is not threaded to
	// this surface, so we gate on the experiment + contentId. Gate inlined per
	// confluence-feature-gating rules.
	if (contentId && expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
		return (
			<AgentSessionsProvider pageId={contentId} onSessionMapChange={setAgentSessionMap}>
				{sidebar}
			</AgentSessionsProvider>
		);
	}

	return sidebar;
};

export const InlineComment: React.ComponentType<Omit<InlineCommentProps, 'flags'>> =
	withFlags(InlineCommentComponent);
